import { Box, Grid } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { usePolicyOutletContext } from "../hooks/usePolicyOutletContext";
import { BoomStats } from "~/pages/platform/policy/components";
import {
  ScopeTable,
  ScopeTableHeader,
  getColumns,
  useEntityScopes,
  useEntityScopesFilterContext,
} from "~/pages/platform/components";
import { organizationMrnFromId, spaceMrnFromId } from "~/hooks/useScope";
import { PolicyScopeNode } from "~/pages/platform/policy/types";
import { ScopeType } from "~/pages/platform/components/ScopeTable/hooks/useEntityScopes";
import { useFetchExceptions } from "~/components/exceptions/use-fetch-exceptions";
import { ExceptionType } from "~/operations";

export interface PolicyOverviewTabProps {
  entityMrn: string;
  defaultScopeType: ScopeType;
  hideOrganizationFilters?: boolean;
}

export function PolicyOverviewTab({
  entityMrn,
  defaultScopeType,
  hideOrganizationFilters,
}: PolicyOverviewTabProps) {
  const { policy } = usePolicyOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const organizationId = searchParams.get("organization");
  const spaceId = searchParams.get("space");
  const viewParam = searchParams.get("view");

  const scopeType: ScopeType = viewParam
    ? (viewParam as ScopeType)
    : spaceId
      ? "Workspaces"
      : organizationId
        ? "Spaces"
        : defaultScopeType;

  const { filterVariables, filterForm } = useEntityScopesFilterContext({
    scopeType,
    findingMrn: policy?.mrn,
    ...(hideOrganizationFilters ? { hideOrganizationFilters: true } : {}),
  });

  const getEntityMrnForScope = () => {
    switch (scopeType) {
      case "Organizations":
        return entityMrn;
      case "Spaces":
        return organizationId
          ? organizationMrnFromId(organizationId)
          : entityMrn;
      case "Workspaces":
        return spaceId ? spaceMrnFromId(spaceId) : entityMrn;
      default:
        return entityMrn;
    }
  };

  const {
    data: filteredData,
    loading,
    totalCount,
    paginationModel,
    onPaginationModelChange,
    rowCount,
    sortModel,
    onSortModelChange,
  } = useEntityScopes<PolicyScopeNode>({
    entityMrn: getEntityMrnForScope(),
    findingMrn: policy.mrn,
    scopeType,
    filterVariables,
  });

  const { exceptionsCount } = useFetchExceptions({
    mrn: policy.mrn,
    scopeMrn: entityMrn,
    types: [ExceptionType.Security],
    includeChildScopes: true,
    includeDeleted: true,
    includeExtension: true,
  });

  const handleScopeTypeChange = (newScopeType: ScopeType) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("view", newScopeType);
      return newParams;
    });
  };

  const handleRowClick = (rowId: string) => {
    const url = new URL(location.href);

    if (scopeType === "Organizations") {
      url.searchParams.append("organization", rowId);
      url.searchParams.delete("view");
      url.searchParams.delete("space");
    } else if (scopeType === "Spaces") {
      url.searchParams.append("space", rowId);
      url.searchParams.delete("view");
    }

    setSearchParams(url.searchParams);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <BoomStats data={filteredData} exceptionsCount={exceptionsCount} />
      <Grid container spacing={2} size={12} flexDirection="column">
        <ScopeTableHeader
          scopeType={scopeType}
          totalCount={totalCount}
          onScopeTypeChange={handleScopeTypeChange}
        />

        {filterForm}

        <ScopeTable
          scopeType={scopeType}
          columns={getColumns(scopeType, "policy")}
          data={filteredData}
          onScopeRowClick={handleRowClick}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          rowCount={rowCount}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
        />
      </Grid>
    </Box>
  );
}
